import { RefObject, useEffect, useMemo, useRef, useState } from "react";

import { useLayoutContext } from "@context/layout";
import {
  getClickScrollOffset,
  getNavOffset,
  getScrollVariantLines,
  getSpyActivateOffset,
  getVisibleCompactHeight,
  observeElementHeight,
} from "../helpers/courseFunction";
import { useIsoMorphicEffect } from "@shared/hooks/useIsoMorphicEffect";

const SITE_HEADER_SELECTOR = "[data-site-header]";
const SIDEBAR_GAP = 24;

interface UseStickyLayoutOffsetsParams {
  isMobile: boolean | null;
  shouldRenderSubscriptionTopBanner: boolean | null;
  spyOffsetRef: RefObject<number>;
}

export const useStickyLayoutOffsets = ({
  isMobile,
  shouldRenderSubscriptionTopBanner,
  spyOffsetRef,
}: UseStickyLayoutOffsetsParams) => {
  const headerRef = useRef<HTMLDivElement | null>(null);
  const stickyHeader = useRef<HTMLDivElement | null>(null);

  const { isAdmin } = useLayoutContext();

  const [isScrollVariant, setIsScrollVariant] = useState(false);
  const [compactHeaderHeight, setCompactHeaderHeight] = useState(0);
  const [siteHeaderHeight, setSiteHeaderHeight] = useState(0);

  useEffect(() => {
    const getSiteHeader = () =>
      document.querySelector<HTMLElement>(SITE_HEADER_SELECTOR);

    return observeElementHeight(getSiteHeader, setSiteHeaderHeight);
  }, []);

  const navOffset = useMemo(
    () =>
      getNavOffset(
        siteHeaderHeight,
        shouldRenderSubscriptionTopBanner,
        isMobile
      ),
    [isMobile, siteHeaderHeight, shouldRenderSubscriptionTopBanner]
  );

  const offsetTop = `${navOffset}px`;
  const sidebarTop = navOffset + SIDEBAR_GAP;
  const visibleCompactHeight = getVisibleCompactHeight(
    isMobile,
    isScrollVariant,
    compactHeaderHeight
  );
  const spyActivateOffset = getSpyActivateOffset(
    navOffset,
    visibleCompactHeight
  );
  const clickScrollOffset = getClickScrollOffset(
    navOffset,
    isMobile,
    isAdmin,
    compactHeaderHeight
  );

  useIsoMorphicEffect(() => {
    spyOffsetRef.current = spyActivateOffset;
  }, [spyActivateOffset]);

  useEffect(() => {
    if (isMobile) {
      setCompactHeaderHeight(0);
      return;
    }

    return observeElementHeight(
      () => stickyHeader.current,
      setCompactHeaderHeight
    );
  }, [isMobile]);

  useEffect(() => {
    const updateScrollVariant = () => {
      const element = headerRef.current;
      if (!element) return;

      const headerBottom = element.getBoundingClientRect().bottom;
      const { showLine, hideLine } = getScrollVariantLines(navOffset);
      const next = isScrollVariant
        ? headerBottom <= hideLine
        : headerBottom <= showLine;

      setIsScrollVariant(next);
    };

    updateScrollVariant();
    window.addEventListener("scroll", updateScrollVariant, { passive: true });
    window.addEventListener("resize", updateScrollVariant);

    return () => {
      window.removeEventListener("scroll", updateScrollVariant);
      window.removeEventListener("resize", updateScrollVariant);
    };
  }, [navOffset]);

  return {
    headerRef,
    stickyHeader,
    isScrollVariant,
    offsetTop,
    sidebarTop,
    navOffset,
    visibleCompactHeight,
    clickScrollOffset,
  };
};
